import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  createFeatureFlag,
  deleteFeatureFlag,
  fetchFeatureFlag,
  fetchFeatureFlags,
  updateFeatureFlag,
} from 'services/backend/featureFlags';
import { ReactQueryFunctionCallbacks } from 'types/internal';

export const featureFlagQueries = {
  all: ['featureFlags'],
  list: () =>
    queryOptions({
      queryFn: fetchFeatureFlags,
      queryKey: featureFlagQueries.all,
      staleTime: 5 * 60 * 1000,
    }),
  get: (opts: { featureFlagId: number }) =>
    queryOptions({
      queryFn: () => fetchFeatureFlag({ featureFlagId: opts.featureFlagId }),
      queryKey: [...featureFlagQueries.all, opts.featureFlagId],
    }),
};

export function useCreateFeatureFlag(
  opts?: ReactQueryFunctionCallbacks<typeof createFeatureFlag>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createFeatureFlag,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: featureFlagQueries.all });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useUpdateFeatureFlag(
  opts?: ReactQueryFunctionCallbacks<typeof updateFeatureFlag>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: updateFeatureFlag,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: featureFlagQueries.all });

      opts?.onSuccess?.(...args);
    },
  });
}

export function useDeleteFeatureFlag(
  opts?: ReactQueryFunctionCallbacks<typeof deleteFeatureFlag>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: deleteFeatureFlag,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: featureFlagQueries.all });

      opts?.onSuccess?.(...args);
    },
  });
}
